'use client';

import { hasObjectKey } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, debounce } from 'es-toolkit';
import { useRef } from 'react';
import { AuthApi, AuthQueryKey, BOOKMARK_TYPE } from '@/api';
import { ROUTES } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { isSameEndpointQuery } from '@/utils/isSameEndpointQuery';
import { useCheckWelloApp } from './useCheckWelloApp';
import { useCustomRouter } from './useCustomRouter';
const WISHLIST_COUNT_KEYS = ['wishlistCount', 'wishlistCnt'] as const;
type BookmarkCountCarrier = Partial<Record<(typeof WISHLIST_COUNT_KEYS)[number], number>> & {
  [key: string]: unknown;
};
interface BookmarkParams {
  /**
   * 쿼리키 조회용
   */
  idx?: string;
  contentId: number | string;
  isBookmarked: boolean;
  bookmarkType: BOOKMARK_TYPE;
}
interface EventDataParams {
  contentId: number | string;
  contentName?: string;
  policyAgency?: string;
  policyAgencyId?: string;
}

// 북마크 가능한 항목의 인터페이스
interface BookmarkableItem {
  id: number;
  wishlistYN: boolean;
  [key: string]: unknown;
}

// 컨텐츠 배열을 포함하는 응답 데이터 인터페이스
interface ContentsResponse {
  context: {
    contents: BookmarkableItem[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}
const OPTIMISTIC_QUERY_KEYS = [AuthQueryKey['get:/wello/v2/auth/member/neighbor/policy'], AuthQueryKey['get:/wello/v2/auth/policy-apply/history'], AuthQueryKey['get:/wello/v2/auth/community/wello-news'], AuthQueryKey['get:/wello/v2/auth/member/recent/policy'], AuthQueryKey['get:/wello/v2/auth/hometown-news'], AuthQueryKey['get:/wello/v2/auth/member/interest/policy'], AuthQueryKey['get:/wello/v2/auth/policy/policy/{metaPolicyIdIdx}/related'], AuthQueryKey['get:/wello/v2/auth/contents/pick/{welloPickId}'], AuthQueryKey['get:/wello/v2/auth/policy'], AuthQueryKey['get:/wello/v2/auth/member/wishlist/policy'], AuthQueryKey['get:/wello/v2/auth/member/wishlist/best-contents'], AuthQueryKey['get:/wello/v2/auth/wello-comment'], AuthQueryKey['get:/wello/v2/auth/member/wishlist/emergency-room'], AuthQueryKey['get:/wello/v2/auth/emergency-room/{emergencyRoomId}'], AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policyId}'], AuthQueryKey['get:/wello/v2/auth/contents/feedback/{bestContentsId}'], AuthQueryKey['get:/wello/v2/auth/wello-comment/my'], AuthQueryKey['get:/wello/v2/auth/community/my'], AuthQueryKey['get:/wello/v2/auth/hometown-news/feedback/{hometownNewsId}'], AuthQueryKey['get:/wello/v2/auth/community/wello-news/feedback/{communityIdIdx}'], AuthQueryKey['get:/wello/v2/auth/member/peer-group/policy'], AuthQueryKey['get:/wello/v2/auth/member/wishlist/hometown-news'], AuthQueryKey['get:/wello/v2/auth/member/wishlist/community/wello-news'], AuthQueryKey['get:/wello/v2/auth/home/institution/policy'], AuthQueryKey['get:/wello/v2/auth/recommend-policy'], AuthQueryKey['get:/wello/v2/auth/tour/feedback/{tourId}']];
type QueryObject = {
  query: {
    page: number;
  };
};
const isQueryObject = (obj: object): obj is QueryObject => {
  return 'query' in obj && typeof (obj as QueryObject).query === 'object';
};

/** 북마크 카운트를 업데이트하는 함수 (가능한 경우) */
const updateWishlistCount = (item: BookmarkCountCarrier, isBookmarked: boolean): void => {
  WISHLIST_COUNT_KEYS.forEach(key => {
    const currentCount = item[key];
    if (typeof currentCount === 'number') {
      item[key] = isBookmarked ? currentCount - 1 : currentCount + 1;
    }
  });
};
export const useBookmark = () => {
  const expectedServerBookmarkedRef = useRef(new Map<BookmarkParams['contentId'], boolean>());
  const debouncedMutateByContentRef = useRef(new Map<BookmarkParams['contentId'], ReturnType<typeof debounce>>());
  const {
    mutate
  } = useMutation({
    mutationFn: ({
      contentId,
      isBookmarked,
      bookmarkType
    }: BookmarkParams) => {
      const isUuidContent = typeof contentId === 'string';
      return AuthApi[isBookmarked ? 'delete:/wello/v2/auth/member/wishlist' : 'post:/wello/v2/auth/member/wishlist']({
        body: {
          contentId: isUuidContent ? undefined : contentId,
          contentUuid: isUuidContent ? contentId : undefined,
          contentTypeCd: bookmarkType
        }
      });
    },
    meta: {
      reset: [AuthQueryKey['get:/wello/v2/auth/member/action-count']()]
    }
  });
  const queryClient = useQueryClient();
  const router = useCustomRouter();
  const {
    isWelloApp
  } = useCheckWelloApp();
  const {
    request: requestHaptic
  } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC
  });
  const mutateRef = useRef(mutate);
  // eslint-disable-next-line react-hooks/refs -- stale closure 방지를 위해 최신 mutate를 ref에 동기화하는 의도적 패턴
  mutateRef.current = mutate;
  const bookmark = (params: BookmarkParams & EventDataParams) => {
    //* 앱은 네이티브 햅틱, 모바일 웹은 Vibration API 폴백 (iOS 브라우저는 미지원으로 무시됨)
    if (isWelloApp) requestHaptic();else navigator.vibrate?.(10);
    const {
      contentId: contentId_0,
      isBookmarked: isBookmarked_0,
      idx,
      contentName,
      bookmarkType: bookmarkType_0,
      policyAgency
    } = params;
    const {
      isLogin
    } = useAuthStore.getState();
    if (!isLogin) return router.push(ROUTES.LOGIN.withSearchParams({
      searchParams: {
        redirect: location.href
      }
    }));
    const contentCategory = {
      [BOOKMARK_TYPE.POLICY]: CONTENT_CATEGORY.POLICY,
      [BOOKMARK_TYPE.POLICY_INFO]: CONTENT_CATEGORY.POLICY_NEWS,
      [BOOKMARK_TYPE.POLICY_CARD]: CONTENT_CATEGORY.POLICY_CARD,
      [BOOKMARK_TYPE.HOMETOWN_NEWS]: CONTENT_CATEGORY.HOMETOWN_NEWS,
      [BOOKMARK_TYPE.EMERGENCY_ROOM]: CONTENT_CATEGORY.EMERGENCY_ROOM,
      [BOOKMARK_TYPE.COMMUNITY]: CONTENT_CATEGORY.COMMUNITY,
      [BOOKMARK_TYPE.TOUR]: CONTENT_CATEGORY.FESTIVAL
    }[bookmarkType_0];
    if (!isBookmarked_0) {
      trackGtmEvent(GTM_EVENT_TYPE.CLICK_BOOKMARK, {
        content_name: contentName,
        content_category: contentCategory,
        content_id: contentId_0.toString(),
        policy_agency: policyAgency,
        policy_agency_id: policyAgency
      });
    }
    const feedQueries = queryClient.getQueriesData({
      predicate: query => query.queryKey[0] === 'feed'
    });
    feedQueries.forEach(([key, data]) => {
      if (!data || typeof data !== 'object' || !('pages' in data) || !Array.isArray((data as {
        pages: unknown[];
      }).pages)) return;
      const newData = cloneDeep(data) as {
        pages: {
          context?: {
            contents?: unknown[];
          };
        }[];
      };
      let hasChange = false;
      newData.pages.forEach(page => {
        const contents = page?.context?.contents;
        if (!Array.isArray(contents)) return;
        contents.forEach(content => {
          if (content && typeof content === 'object' && ('contentId' in content && content.contentId === contentId_0 || 'contentIdx' in content && content.contentIdx === contentId_0) && 'wishlistYN' in content && typeof (content as {
            wishlistYN: unknown;
          }).wishlistYN === 'boolean') {
            (content as {
              wishlistYN: boolean;
            }).wishlistYN = !isBookmarked_0;
            hasChange = true;
          }
        });
      });
      if (hasChange) {
        queryClient.setQueryData(key, newData);
      }
    });
    const bookmarkQueries = queryClient.getQueriesData({
      predicate: query_0 => OPTIMISTIC_QUERY_KEYS.some(queryKey => isSameEndpointQuery(query_0, queryKey()))
    });
    bookmarkQueries.forEach(([key_0, data_0]) => {
      const isBaseKeyHasId = (() => {
        const [firstKey] = key_0;
        const isV2ApiQueryKey = typeof firstKey === 'object';
        if (isV2ApiQueryKey) {
          const StringKey = JSON.stringify(firstKey);
          const id = String(contentId_0);
          if (StringKey.includes(id) || idx && StringKey.includes(idx)) return true;
        }
        return false;
      })();
      if ((key_0.includes(contentId_0) || isBaseKeyHasId || idx && key_0.includes(idx)) && hasObjectKey(data_0, 'context') && hasObjectKey(data_0.context, 'wishlistYN')) {
        const newData_0 = cloneDeep(data_0);
        if (hasObjectKey(newData_0.context, 'wishlistYN') && typeof newData_0.context.wishlistYN === 'boolean') {
          newData_0.context.wishlistYN = !isBookmarked_0;
          updateWishlistCount(newData_0.context, isBookmarked_0);
          queryClient.setQueryData(key_0, newData_0);
        }
      }
      //* 일반 쿼리 (not Infinite Query)
      else if (data_0 && typeof data_0 === 'object' && hasObjectKey(data_0, 'context') && data_0.context && typeof data_0.context === 'object') {
        const newData_1 = cloneDeep(data_0);
        const updateContents = (contents_0: unknown[]): unknown[] => contents_0.map(item => {
          if (item && typeof item === 'object' && 'id' in item && typeof item.id === 'number' && item.id === contentId_0 && 'wishlistYN' in item && typeof item.wishlistYN === 'boolean') {
            updateWishlistCount(item, isBookmarked_0);
            return {
              ...item,
              wishlistYN: !isBookmarked_0
            };
          }
          return item;
        });
        const hasContents = (obj: unknown): obj is ContentsResponse => obj !== null && typeof obj === 'object' && 'context' in obj && obj.context !== null && typeof obj.context === 'object' && 'contents' in obj.context && Array.isArray(obj.context.contents);
        let hasAnyChanges = false;
        if (hasContents(newData_1)) {
          // 1. contents 배열 업데이트
          const updatedContents = updateContents(newData_1.context.contents);
          const hasChanges = updatedContents.some((item_0, index) => item_0 !== newData_1.context.contents[index]);
          if (hasChanges) {
            newData_1.context.contents = updatedContents as BookmarkableItem[];
            hasAnyChanges = true;
          }
        }

        // 2. 타임라인 필드들 업데이트 (past_contents, deadline_contents, future_contents)
        const timelineFields = ['pastContents', 'deadlineContents', 'futureContents'] as const;
        if (typeof newData_1.context === 'object' && newData_1.context !== null) {
          const context = newData_1.context as Record<string, unknown>;
          for (const field of timelineFields) {
            if (hasObjectKey(context, field) && Array.isArray(context[field])) {
              const originalContents = context[field] as unknown[];
              const updatedFieldContents = updateContents(originalContents);
              const hasChanges_0 = updatedFieldContents.some((item_1, index_0) => item_1 !== originalContents[index_0]);
              if (hasChanges_0) {
                context[field] = updatedFieldContents as BookmarkableItem[];
                hasAnyChanges = true;
              }
            }
          }
        }
        if (hasAnyChanges) {
          // 맞춤정책(/policy) scrap_count 업데이트 (정책 북마크인 경우)
          if (bookmarkType_0 === BOOKMARK_TYPE.POLICY && hasObjectKey(newData_1.context, 'scrapCount') && typeof newData_1.context.scrapCount === 'number') {
            newData_1.context.scrapCount = isBookmarked_0 ? newData_1.context.scrapCount - 1 : newData_1.context.scrapCount + 1;
          }
          queryClient.setQueryData(key_0, newData_1);
        }
      } else for (const idKey of ['communityId', 'bestContentsId', 'hometownNewsId', 'welloCommentId', 'policyId', 'id'] as const) {
        const newData_2 = cloneDeep(data_0);
        const targetObjectList = (data_1 => {
          const result: unknown[] = [];
          const findTargetObject = (obj_0: unknown) => {
            if (obj_0 && typeof obj_0 === 'object') {
              if (hasObjectKey(obj_0, idKey) && obj_0[idKey] === contentId_0) {
                switch (idKey) {
                  case 'id':
                    {
                      const hasId = 'metaPolicyIdIdx' in obj_0 || 'uuid' in obj_0;
                      if (hasId) result.push(obj_0);
                      break;
                    }
                  default:
                    result.push(obj_0);
                    break;
                }
              }
              for (const key_1 in obj_0) {
                if (hasObjectKey(obj_0, key_1)) {
                  findTargetObject(obj_0[key_1]);
                }
              }
            }
          };
          findTargetObject(data_1);
          return result;
        })(newData_2);
        const changeBookmarkCount = (obj_1: unknown) => {
          if (obj_1 && typeof obj_1 === 'object') {
            updateWishlistCount(obj_1 as BookmarkCountCarrier, isBookmarked_0);
            for (const key_2 in obj_1) {
              if (hasObjectKey(obj_1, key_2)) {
                changeBookmarkCount(obj_1[key_2]);
              }
            }
          }
        };
        const changeBookmarkYN = (obj_2: unknown) => {
          if (obj_2 && typeof obj_2 === 'object') {
            if (hasObjectKey(obj_2, 'wishlistYN') && typeof obj_2.wishlistYN === 'boolean') {
              obj_2['wishlistYN'] = !isBookmarked_0;
            }
            for (const key_3 in obj_2) {
              if (hasObjectKey(obj_2, key_3)) {
                changeBookmarkYN(obj_2[key_3]);
              }
            }
          }
        };
        targetObjectList.forEach(obj_3 => {
          changeBookmarkCount(obj_3);
          changeBookmarkYN(obj_3);
          const [firstKey_0] = key_0;
          const isV2InfiniteQuery = firstKey_0 && isQueryObject(firstKey_0) && '_infinite';
          if (isV2InfiniteQuery) {
            firstKey_0.query.page = 1;
          }
          queryClient.setQueryData(key_0, newData_2);
        });
      }
    });
    if (!expectedServerBookmarkedRef.current.has(contentId_0)) {
      expectedServerBookmarkedRef.current.set(contentId_0, isBookmarked_0);
    }
    let debouncedMutate = debouncedMutateByContentRef.current.get(contentId_0);
    if (!debouncedMutate) {
      //* 콘텐츠별로 디바운스를 분리해 서로 다른 콘텐츠 연속 탭 시 요청 유실 방지
      debouncedMutate = debounce<typeof mutate>((latestParams, options) => {
        const expectedServerBookmarked = expectedServerBookmarkedRef.current.get(latestParams.contentId);
        const desiredBookmarked = !latestParams.isBookmarked;

        //* 사용자 최종 의도가 서버 기대 상태와 같으면(토글 원복) 요청 생략
        if (expectedServerBookmarked === desiredBookmarked) {
          expectedServerBookmarkedRef.current.delete(latestParams.contentId);
          return;
        }
        expectedServerBookmarkedRef.current.set(latestParams.contentId, desiredBookmarked);
        return mutateRef.current(latestParams, options);
      }, 500);
      debouncedMutateByContentRef.current.set(contentId_0, debouncedMutate);
    }
    debouncedMutate(params, {
      onError: () => {
        //* 쿼리 오류 시 북마크 관련 쿼리 무효화 (옵티미스틱 업데이트 취소)
        expectedServerBookmarkedRef.current.delete(contentId_0);
        bookmarkQueries.forEach(([key_4]) => {
          queryClient.invalidateQueries({
            queryKey: key_4
          });
        });
      }
    });
  };
  return {
    bookmark
  };
};